import { useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  getModuleHref,
  moduleRegistry,
  useActiveModule,
  type ModuleInstance,
} from "@/modules";
import { isDesktopViewport } from "@/hooks/use-is-desktop";
import { useChatDockStore } from "@/features/chat-dock/store/chat-dock-store";
import {
  useModuleTransitionStore,
  type VeilRect,
} from "../store/module-transition-store";

const HUE_VAR = "--module-hue";

/**
 * Kart tıklanabilir mi? Aktif modüller her zaman; `soon` ama blueprint'i
 * hazır olanlar (ör. technology) yalnızca dev'de — route'ları zaten mount
 * olduğu için gerçek kataloğa bağlanmadan prova edilebilir.
 */
export const canOpenModule = (instance: ModuleInstance): boolean =>
  instance.status !== "soon" || (import.meta.env.DEV && Boolean(instance.blueprint));

export interface SelectOptions {
  /** Seçilen modülün dock'unu boş composer'la aç (Launchpad "yeni sohbet"). */
  newChat?: boolean;
}

const rectOf = (el: HTMLElement): VeilRect => {
  const r = el.getBoundingClientRect();
  return {
    top: r.top,
    left: r.left,
    width: r.width,
    height: r.height,
    radius: parseFloat(getComputedStyle(el).borderTopLeftRadius) || 0,
  };
};

export const prefersReducedMotion = (): boolean =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Hub'ın tüm davranışı: sıralı modül listesi, hover'da tema önizlemesi,
 * seçimde perde geçişi. Kartlar prop almaz; buradan çeker.
 */
/**
 * Sayfa seviyesinde bir kez: hub/anasayfa/launchpad'den çıkarken tema
 * önizlemesi ve hover kalıntısı bırakma. useModuleHub birden çok yerde
 * (Canvas içi dahil) çağrıldığı için cleanup oraya değil buraya konur.
 */
export const useModuleHubCleanup = () => {
  const { previewHue } = useModuleHub();
  const setHoveredId = useModuleTransitionStore((s) => s.setHoveredId);
  useEffect(
    () => () => {
      previewHue(null);
      setHoveredId(null);
    },
    [previewHue, setHoveredId],
  );
};

export const useModuleHub = () => {
  const navigate = useNavigate();
  const active = useActiveModule();
  const veil = useModuleTransitionStore((s) => s.veil);
  const startVeil = useModuleTransitionStore((s) => s.startVeil);
  const hoveredId = useModuleTransitionStore((s) => s.hoveredId);
  const setHoveredId = useModuleTransitionStore((s) => s.setHoveredId);

  // Aktif modüller önce, "yakında" olanlar sonra; registry sırası korunur
  const modules = useMemo(() => {
    const list = [...moduleRegistry.instances];
    return [
      ...list.filter((m) => m.status !== "soon"),
      ...list.filter((m) => m.status === "soon"),
    ];
  }, []);

  /**
   * Hover önizlemesi: <html>'deki --module-hue geçici olarak kartın hue'suna
   * çekilir; kayıtlı property olduğu için CSS transition ile süpürülür.
   * Aktif modül varken ModuleThemeSync sahibi olduğu için dokunulmaz.
   */
  const previewHue = useCallback(
    (hue: number | null) => {
      const root = document.documentElement;
      // data-module varsa hue'nun sahibi ModuleThemeSync'tir — özellikle hub
      // unmount cleanup'ı yeni modülün hue'su yazıldıktan sonra çalışır.
      if (active || root.dataset.module) return;
      if (hue === null) root.style.removeProperty(HUE_VAR);
      else root.style.setProperty(HUE_VAR, String(hue));
    },
    [active],
  );

  /**
   * Hover'daki modül (store'da; Canvas ve DOM ağaçları paylaşır) + tema
   * önizlemesi tek adımda. "Yakında" modüller hover almaz.
   */
  const setHovered = useCallback(
    (instance: ModuleInstance, hover: boolean) => {
      if (!canOpenModule(instance)) return;
      const cur = useModuleTransitionStore.getState().hoveredId;
      if (hover) setHoveredId(instance.id);
      else if (cur === instance.id) setHoveredId(null);
      previewHue(hover ? instance.theme.hue : null);
    },
    [previewHue, setHoveredId],
  );

  /**
   * Modüle geç. `origin` perdenin büyümeye başlayacağı yer: bir DOM kartı
   * ya da (3D sahnedeki obje gibi) hesaplanmış bir dikdörtgen; null ise
   * perdesiz düz geçiş. `newChat` ile modülün dock'u boş composer'la açılır
   * ("Yeni sohbet" → Launchpad akışı).
   */
  const select = useCallback(
    (
      instance: ModuleInstance,
      origin: HTMLElement | VeilRect | null,
      options: SelectOptions = {},
    ) => {
      if (!canOpenModule(instance) || veil) return;
      const href = getModuleHref(instance);
      setHoveredId(null);

      if (options.newChat) {
        const dock = useChatDockStore.getState();
        dock.setModuleChat(instance.id, null);
        // Mobilde sheet tam ekranı kaplar; kullanıcı tutamaçtan açar
        if (isDesktopViewport()) dock.open();
      }

      if (!origin || prefersReducedMotion()) {
        previewHue(null);
        if (/^https?:\/\//.test(href)) window.location.assign(href);
        else navigate(href);
        return;
      }

      const rect: VeilRect =
        origin instanceof HTMLElement ? rectOf(origin) : origin;
      startVeil({ moduleId: instance.id, hue: instance.theme.hue, href, rect });
    },
    [veil, navigate, previewHue, startVeil, setHoveredId],
  );

  return {
    modules,
    /** Perde açıkken kartlar tekrar tıklanamaz. */
    isTransitioning: veil !== null,
    selectedId: veil?.moduleId ?? null,
    hoveredId,
    setHovered,
    previewHue,
    select,
  };
};
